import { AgentOrderDetailModel } from '@/__generated';
import CancelMultiBookingsModal from '@/components/App/BookingComponents/CancelMultiBookingsModal';
import SamePaxAndNextBookingButton from '@/components/App/BookingComponents/SamePaxAndNextBookingButton';
import HotelJapaneseName from '@/components/App/Hotels/HotelJapaneseName';
import SingleOrPluralText from '@/components/App/SingleOrPluralText';
import Spin from '@/components/Spin';
import { HOTEL_CONFIG, UNKNOWN_ERROR_MESSAGE, appDisplayDateFormat } from '@/config';
import { BookingSource, BookingStatus, HotelRoomStatus, PaymentStatus } from '@/constants';
import { ModalUtils } from '@/contexts/ModalWrapper';
import { formatPriceWithCurrency } from '@/hooks/useCurrencyFormat';
import { useGetUseRole } from '@/hooks/useGetUserRole';
import { apiSdk } from '@/httpClient';
import { AppUrls } from '@/urls';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { faCalculator, faCancel, faCheck, faLocation, faMapPin, faReceipt, faUserAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { bind } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import ImageGallery from 'react-image-gallery';
import { exportService } from '@/services/exportService';
import bookingService from '@/services/bookingService';
import PriceOrTBA from '@/components/App/BookingComponents/PriceOrTBA';

const ExportOrderDetailLazyButton = React.lazy(() => import('@/components/App/BookingComponents/ExportOrderDetailButton'));

type Props = {};

export default function OrderDetailPage({}: Props) {
  const { orderId } = useParams<{ orderId: string }>();
  const [loading, setLoading] = React.useState<boolean>(true);
  const history = useHistory();
  const [orderDetail, setOrderDetail] = React.useState<AgentOrderDetailModel>();
  const [selectedBookingIds, setSelectedBookingIds] = React.useState<string[]>([]);
  const [displayCancelBookingsModal, setDisplayCancelBookingsModal] = React.useState(false);
  const { isAdmin, isBooking } = useGetUseRole();
  if (!isAdmin && !isBooking) {
    history.push(AppUrls.Page401);
    return null;
  }
  React.useEffect(() => {
    if (orderId) {
      getData();
    } else {
      history.push(AppUrls.HomePage);
    }
  }, [orderId]);

  const getData = () => {
    apiSdk.AgentOrderBookingService.getOrderDetails({ orderId: orderId })
      .then(res => {
        setOrderDetail(res);
        setSelectedBookingIds(res.bookings?.map(b => b.id || '') || []);
      })
      .catch(err => {
        ModalUtils.error({ content: t(err.message || UNKNOWN_ERROR_MESSAGE) });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const { t } = useTranslation();
  const lastBooking = React.useMemo(() => {
    if (orderDetail?.bookings?.length) {
      return orderDetail.bookings[0];
    }
    return null;
  }, [orderDetail]);

  const onSelectedAllChange = () => {
    if (isCheckedAll) {
      setSelectedBookingIds([]);
    } else {
      setSelectedBookingIds(orderDetail?.bookings?.map(b => b.id || '') || []);
    }
  };
  const onSelectedChange = (id: string) => {
    if (selectedBookingIds.includes(id)) {
      setSelectedBookingIds(selectedBookingIds.filter(x => x !== id));
    } else {
      setSelectedBookingIds(x => [...x, id]);
    }
  };
  const isCheckedAll = React.useMemo(() => {
    return orderDetail?.bookings?.length === selectedBookingIds.length;
  }, [orderDetail, selectedBookingIds]);

  const canCancelBooking = React.useMemo(() => {
    return orderDetail?.bookings?.some(x => [BookingSource.Omega].includes(x.source as any));
  }, [orderDetail]);
  const canAddSameGuest = React.useMemo(() => {
    return orderDetail?.bookings?.some(x => [BookingSource.Omega].includes(x.source as any));
  }, [orderDetail]);
  const cancelBookings = () => {
    let bookings = orderDetail?.bookings?.filter(x => selectedBookingIds.includes(x.id as any));

    let rooms: Array<any> = [];
    bookings?.forEach(booking => {
      booking.roomStatus?.forEach((status, index) => {
        rooms.push({
          checkInDate: booking?.checkinDate,
          supplier: booking.roomSuppliers?.at(index) as any,
          status: status as any,
        });
      });
    });
    bookingService.handleOnCancelBooking(rooms).then(next => {
      if (next) {
        setDisplayCancelBookingsModal(true);
      }
    });
  };
  const downloadOrderReceipt = () => {
    exportService.downloadOrderReceipt(orderId);
  };

  return (
    <Spin loading={loading}>
      <div className="row y-gap-20 justify-between items-end pb-10 lg:pb-40 md:pb-32">
        <div className="col-12">
          <h1 className="text-30 lh-14 fw-600">{`Order detail: ${orderId}`}</h1>
          <div className="text-15 fw-500 text-blue-1 d-flex items-center">
            <FontAwesomeIcon icon={faUserAlt} className="mr-5" />
            <span>{`Lead pax: ${lastBooking?.leadPassenger}`}</span>
          </div>
        </div>
      </div>
      {/* action */}
      {selectedBookingIds?.length && displayCancelBookingsModal ? (
        <CancelMultiBookingsModal
          bookingIds={selectedBookingIds}
          orderId={orderId}
          onCloseModal={reload => {
            setDisplayCancelBookingsModal(false);
            if (reload) {
              getData();
            }
          }}
        />
      ) : null}
      {orderDetail ? (
        <div className="py-10 px-10 rounded-4 bg-white justify-content-between d-flex">
          <div className="d-flex items-center">
            <div className="form-checkbox d-flex items-center mr-20 ml-10 text-dark-1">
              <input type="checkbox" checked={isCheckedAll} onClick={onSelectedAllChange} />
              <div className="form-checkbox__mark">
                <div className="form-checkbox__icon icon-check"></div>
              </div>
              <div className="text-15 fw-500 ml-10">Check all</div>
            </div>

            <React.Suspense>
              <ExportOrderDetailLazyButton order={orderDetail} bookingIds={selectedBookingIds} />
            </React.Suspense>
            <button className={'button text-white px-20 py-15 -dark-1 bg-dark-1 text-dark-1 mr-5'} onClick={downloadOrderReceipt}>
              <FontAwesomeIcon icon={faReceipt} className="mr-5" />
              Download Receipt
            </button>
            {canCancelBooking ? (
              <button
                disabled={!selectedBookingIds?.length}
                className={
                  !selectedBookingIds?.length
                    ? 'button px-20 py-15 bg-light-2 mr-5 text-white'
                    : 'button text-white px-20 py-15 -dark-1 bg-red-1 text-dark-1 mr-5'
                }
                onClick={cancelBookings}
              >
                <FontAwesomeIcon icon={faCancel} className="mr-5" />
                Cancel
              </button>
            ) : null}
          </div>
          {lastBooking?.refNumber && canAddSameGuest ? (
            <SamePaxAndNextBookingButton bookingRefNumber={lastBooking?.refNumber} rooms={orderDetail?.roomSearchInfos || []} />
          ) : null}
        </div>
      ) : null}

      <div className="mt-20 ">
        {orderDetail?.bookings?.map(booking => {
          const isChecked = selectedBookingIds.includes(booking.id as any);
          const mainImage = booking.hotelImages?.length ? booking.hotelImages[0] : HOTEL_CONFIG.defaultImage;
          return (
            <div key={booking.id} className="row mt-10 x-gap-0 rounded-4 bg-white shadow-3 mt-10">
              <div className="col-auto px-30 d-flex items-center">
                <button className=" form-checkbox">
                  <input type="checkbox" checked={isChecked} onClick={() => onSelectedChange(booking.id || '')} />
                  <div className="form-checkbox__mark">
                    <div className="form-checkbox__icon icon-check"></div>
                  </div>
                </button>
              </div>
              <div className="col-auto position-relative " style={{ width: 200 }}>
                <img className="w-100 aspect-ratio-1-1 col-12 rounded-4" src={mainImage} alt="hotel" />
              </div>
              <div className="col px-20 py-10">
                <div className="text-18 lh-16 fw-500">{booking.hotelName}</div>
                <div className="text-14">
                  <i className="icon-location-2 mr-5 text-light-1" />
                  {booking.hotelAddress}
                </div>
                <div className="text-14 text-light-1 d-flex items-center">
                  <FontAwesomeIcon icon={faCalendar} className="mr-5" />
                  <div className="w-100px">Booking date:</div>
                  <span className="fw-500 text-dark-1">{`${appDisplayDateFormat.default.dateOnly(booking.bookingDate)}`}</span>
                </div>
                <div className="text-14 text-light-1 d-flex items-center">
                  <FontAwesomeIcon icon={faCalendar} className="mr-5" />
                  <div className="w-100px">Check-in Date:</div>
                  <span className="fw-500 text-dark-1">{`${appDisplayDateFormat.default.dateOnly(booking.checkinDate)}`}</span>
                </div>
                <div className="text-14 text-light-1 d-flex items-center">
                  <FontAwesomeIcon icon={faCalendar} className="mr-5" />
                  <div className="w-100px">Check-out Date:</div>
                  <span className="fw-500 text-dark-1">{`${appDisplayDateFormat.default.dateOnly(booking.checkoutDate)}`}</span>
                </div>
                {booking.onHoldPaymentDeadline ? (
                  <div className="text-14 text-light-1 d-flex items-center">
                    <FontAwesomeIcon icon={faCalendar} className="mr-5" />
                    <div>On hold payment deadline:</div>
                    <span className="fw-500 text-dark-1">{`${appDisplayDateFormat.default.withTime(booking.onHoldPaymentDeadline)}`}</span>
                  </div>
                ) : null}

                <div className="text-14 text-light-1 d-flex items-center">
                  <FontAwesomeIcon icon={faUserAlt} className="mr-5" />
                  <div className="w-100px">Lead pax:</div>
                  <span className="fw-500 text-dark-1">{booking.leadPassenger}</span>
                </div>
              </div>
              <div className="col-2  border-left-light items-center">
                <div className="text-15 fw-500 mt-40 text-center">Booking Status</div>
                <div className="fw-500 text-18 text-center" style={{ color: BookingStatus.__getColor(booking.bookingStatus) }}>
                  {BookingStatus.__getValue(booking.bookingStatus)}
                </div>
              </div>
              <div className="col-2  border-left-light  items-center">
                <div className="text-15 fw-500 mt-40 text-center">Payment Status</div>
                <div className="fw-500 text-18 text-center" style={{ color: PaymentStatus.__getColor(booking.paymentStatus) }}>
                  {PaymentStatus.__getValue(booking.paymentStatus)}
                </div>
              </div>
              <div className="col-auto pl-70 text-right md:text-left px-20 py-10 border-left-light">
                <div className="text-14 text-blue-1 fw-500 text-18">{booking.refNumber}</div>
                {booking.agentBookingRef ? <div className="text-14 fw-500 text-14">{booking.agentBookingRef}</div> : null}

                <div className="text-14">
                  <SingleOrPluralText count={booking.adultCount || 0} pluralText="{} adults" singleText="{} adult" />
                  {`, ${booking.childCount} child`}
                </div>
                <div className="text-22 lh-12 fw-600 mt-5">
                  {' '}
                  <PriceOrTBA
                    cancelledAt={booking.cancelledAt}
                    currency={booking.currency}
                    faCancelChargeDate={booking.faCancelChargeDate}
                    price={booking.sellingPrice}
                    source={booking.source}
                  />
                </div>
                <a
                  target="_blank"
                  className="button  -md -dark-1 bg-blue-1 text-white mt-10"
                  href={AppUrls.__createBookingDetailUrl(booking.id || '')}
                >
                  View detail <i className="icon-arrow-top-right ml-15"></i>
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </Spin>
  );
}
